// src/components/GarbageAlert/GarbageAlertsView.js
// User-friendly view for managing garbage truck alerts

import React, { useState, useEffect } from 'react';
import { API } from '../../utils/config';
import GarbageAlertForm from './GarbageAlertForm';

const GarbageAlertsView = ({ enableSwipe, disableSwipe }) => {
  const [alerts, setAlerts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);

  // Manage swipe behavior for modals
  const anyModalOpen = showCreateForm || editingAlert;

  useEffect(() => {
    if (!enableSwipe || !disableSwipe) return;
    anyModalOpen ? disableSwipe() : enableSwipe();
  }, [anyModalOpen, enableSwipe, disableSwipe]);

  useEffect(() => {
    fetchAlerts();
    fetchRoutes();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API}/garbage-alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      } else {
        // If endpoint doesn't exist yet, show empty state
        setAlerts([]);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await fetch(`${API}/van-routes/active`);
      if (response.ok) {
        setRoutes(await response.json());
      }
    } catch (err) {
      console.error('Error fetching routes:', err);
    }
  };

  const getRouteName = (routeId) => {
    const route = routes.find(r => r.id === routeId);
    return route ? route.name : 'Unknown';
  };

  const handleToggleAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API}/garbage-alerts/${alertId}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchAlerts();
      }
    } catch (err) {
      console.error('Error toggling alert:', err);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API}/garbage-alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchAlerts();
      }
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    setEditingAlert(null);
    fetchAlerts();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center justify-center">
          <span className="mr-2">🔔</span>
          Garbage Truck Alerts
        </h1>
        <p className="text-gray-600 mt-1">
          Get notified when the garbage truck is approaching your home
        </p>
      </div>

      {/* Create Alert Button */}
      <button
        onClick={() => setShowCreateForm(true)}
        className="w-full mb-6 px-6 py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl hover:from-green-700 hover:to-teal-700 transition-all shadow-lg flex items-center justify-center font-medium text-lg"
      >
        <span className="mr-2 text-2xl">+</span>
        Create New Alert
      </button>

      {/* Empty State */}
      {alerts.length === 0 && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="text-6xl mb-4">🚛</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Alerts Yet</h3>
          <p className="text-gray-600 mb-4">
            Create an alert to get notified when the garbage truck is near your home.
          </p>
          <div className="bg-white rounded-lg p-4 text-left text-sm text-gray-600">
            <p className="font-medium text-gray-800 mb-2">How it works:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Set your home location on the map</li>
              <li>Choose how close the truck should be before alerting you</li>
              <li>Select which days you want to be notified</li>
              <li>Get a notification when the truck is approaching!</li>
            </ol>
          </div>
        </div>
      )}

      {/* Alerts List */}
      {alerts.length > 0 && (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all ${
                alert.is_active ? 'border-green-200' : 'border-gray-200 opacity-60'
              }`}
            >
              {/* Alert Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                    alert.is_active ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {alert.is_active ? '🔔' : '🔕'}
                  </div>
                  <div className="ml-3">
                    <h3 className="font-bold text-gray-800">
                      {alert.name || 'My Alert'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      🚛 {getRouteName(alert.route_id)} Route
                    </p>
                  </div>
                </div>

                {/* Toggle Switch */}
                <button
                  onClick={() => handleToggleAlert(alert.id)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    alert.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                    alert.is_active ? 'right-1' : 'left-1'
                  }`} />
                </button>
              </div>

              {/* Alert Details */}
              <div className="px-4 pb-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Distance</p>
                  <p className="font-bold text-gray-800">{alert.alert_distance_feet || 500} ft</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Notice</p>
                  <p className="font-bold text-gray-800">{alert.notify_minutes_before || 10} min</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Days</p>
                  <p className="font-bold text-gray-800 text-xs">
                    {alert.notification_days?.join(', ') || 'All'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-4 pb-4 flex gap-2">
                <button
                  onClick={() => setEditingAlert(alert)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteAlert(alert.id)}
                  className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Card */}
      <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start">
          <span className="text-2xl mr-3">💡</span>
          <div>
            <h4 className="font-medium text-blue-800">Tip</h4>
            <p className="text-sm text-blue-700">
              Make sure you have notifications enabled in your browser settings to receive alerts when the garbage truck is near.
            </p>
          </div>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingAlert) && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <GarbageAlertForm
              existingAlert={editingAlert}
              onClose={() => {
                setShowCreateForm(false);
                setEditingAlert(null);
              }}
              onSuccess={handleFormSuccess}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GarbageAlertsView;
